import { MOB_TYPES } from '@airmash/protocol';
import type { HowlOptions } from 'howler';
import { Howl } from 'howler';
import { clock } from './Clock';
import { GAME_DEFAULTS } from './Config';
import { PlaneType } from './Constants';
import { device } from './Device';
import { getCamera as graphicsGetCamera, viewport } from './Graphics';
import { hasFocus as inputHasFocus } from './Input';
import type { Mob } from './Mob';
import type Player from './Player';
import { setSettings, settings } from './Settings';
import { clamp as toolsClamp, rand as toolsRand, randInt as toolsRandInt } from './Tools';
import { updateSound as uiUpdateSound } from './UI';
import type Vector from './Vector';

// Types
type SoundSprite = [number, number] | [number, number, true];
type FadeParams = [number, number, number] | [number, number, number, true];
type SoundKind = 'launches' | 'uiclick' | 'upgrades' | 'playerkills' | 'mobexplosions';

interface ThrusterInfo {
  kind: 'player' | 'missile';
  started: number;
  last: number;
  soundId: number;
  vol: number;
}

const MIN_AUDIBLE_VOLUME = 0.01;
const explosionSpriteNames: readonly MainSpriteName[] = [
  'explosion1',
  'explosion2',
  'explosion3',
  'explosion4',
  'explosion5',
  'explosion6',
];
const launchSpriteNames: readonly MainSpriteName[] = ['launch1', 'launch2'];

// State variables
let mainHowlInstance: Howl | undefined;
let flagVoiceHowl: Howl | undefined;

/** Sprite map for `flag_phrases_1.mp3` — 6 CTF events × 2 random variants.
 * Offsets/lengths in ms; harvested from SWAM (engine-swam-only.js:11062). */
const flagVoiceSprites = {
  EnemyTaken_1: [0, 1450],
  OursTaken_1: [1500, 1800],
  EnemyCaptured_1: [3300, 1800],
  OursCaptured_1: [5200, 1900],
  EnemyRecovered_1: [7300, 1600],
  OursRecovered_1: [9000, 1800],
  EnemyTaken_2: [11000, 1500],
  OursTaken_2: [12600, 1800],
  EnemyCaptured_2: [14600, 1600],
  OursCaptured_2: [16400, 1800],
  EnemyRecovered_2: [18400, 1600],
  OursRecovered_2: [20200, 1800],
} satisfies Record<string, SoundSprite>;

/** A voice line in `flag_phrases_1.mp3`. */
type FlagVoiceSpriteName = keyof typeof flagVoiceSprites;

const flagVoiceConfig: HowlOptions = {
  src: ['/audio/flag_phrases_1.mp3'],
  volume: 0.5,
  sprite: flagVoiceSprites,
};
const thrustersByName = new Map<string, ThrusterInfo>();
let playerThrustersPlayingCount = 0;
let missileThrustersPlayingCount = 0;
/** Howler ids of thruster sounds fading out, each with the clock.time at which it is stopped. */
const thrusterStopTimeBySoundId = new Map<number, number>();
let lastSoundscapeUpdateTime = 0;

// Reusable fade parameter buffer — consumed synchronously, never stored
const _fade3: [number, number, number] = [0, 0, 0];
const _fade4: [number, number, number, true] = [0, 0, 0, true];

function fade3(from: number, to: number, duration: number) {
  _fade3[0] = from;
  _fade3[1] = to;
  _fade3[2] = duration;
  return _fade3;
}

function fade4(from: number, to: number, duration: number) {
  _fade4[0] = from;
  _fade4[1] = to;
  _fade4[2] = duration;
  return _fade4;
}
const playHistoryByKind: Partial<Record<SoundKind, { num: number; time: number }>> = {};
const mobExplosionBaseVolume = 0.3;
const playerKillBaseVolume = 1;
const missileLaunchBaseVolume = 0.3;
const playerThrusterVolume = 0.06;
const missileThrusterVolume = 0.08;
const playerImpactSpeedVolumeBoost = 0.05;
const powerupBaseVolume = 0.25;
const repelBaseVolume = 0.3;
const explosionVolumeByMobType = [0, 0.7, 1, 0.4, 0, 0.7, 0.4, 0.7];
const launchVolumeByMissileType = [0, 0.7, 1, 0.4, 0, 0.7, 1, 0.7];
const launchPlaybackRateByMissileType = [0, 0.8, 0.5, 1, 0, 0.8, 0.5, 0.8];
const playerKillVolumeByPlaneType = [0, 0.8, 1, 0.7, 0.8, 0.8];
const thrusterPlaybackRateByPlaneType = [0, 1.5, 1.2, 0.8, 1.35, 1.7];
const thrusterVolumeByPlaneType = [0, 1, 1, 0.35, 1, 1];
const thrusterPlaybackRateByMobType = [0, 0.8, 0.5, 1, 0, 0.8, 0.8, 0.8];
/** Impact playback rate by the hit player's plane type; legacy named it by missile type but every caller passed the plane. */
const impactPlaybackRateByPlaneType = [0, 1.5, 0.8, 2, 1, 1.5];
const customVolumeBySpriteName: Partial<Record<MainSpriteName, number>> = {
  powerup_shield: 0.5,
  upgrade: 0.2,
  complete: 0.5,
  levelup: 0.5,
  respawn: 0.05,
  click: 0.5,
};
/** Sprite table of `sounds.mp3`: offsets and lengths in ms, `true` marks a loop. */
const mainSprites = {
  chopper: [0, 3206.1451247165533, true],
  click: [5e3, 374.9886621315195],
  complete: [7e3, 1910.9750566893417],
  explosion1: [1e4, 4137.460317460316],
  explosion2: [16e3, 4155.44217687075],
  explosion3: [22e3, 4168.367346938776],
  explosion4: [28e3, 4580.272108843537],
  explosion5: [34e3, 4144.943310657595],
  explosion6: [4e4, 4191.360544217688],
  impact: [46e3, 3730.7709750566855],
  launch1: [51e3, 1492.4943310657568],
  launch2: [54e3, 1511.2244897959215],
  levelup: [57e3, 1886.4172335600883],
  missile: [6e4, 8335.351473922898, true],
  powerup_rampage: [7e4, 7423.083900226758],
  powerup_shield: [79e3, 2070.4761904761854],
  powerup_upgrade: [83e3, 1640.8616780045406],
  repel: [86e3, 2e3],
  respawn: [89e3, 1315.9410430838961],
  thruster: [92e3, 9499.841269841269, true],
  upgrade: [103e3, 2062.517006802722],
} satisfies Record<string, SoundSprite>;

/** A sound in `sounds.mp3`; every play call is checked against the table. */
type MainSpriteName = keyof typeof mainSprites;

const mainHowlConfig: HowlOptions = {
  src: ['/audio/sounds.mp3?4'],
  volume: 0,
  sprite: mainSprites,
};

/** Initializes the sound system with the Howl sprite sheet. */
export function setup() {
  mainHowlInstance = new Howl(mainHowlConfig);
  flagVoiceHowl = new Howl(flagVoiceConfig);
}

/**
 * Plays a CTF flag-event voice line. Picks `_1` or `_2` randomly per call
 * for variety. Gated by both global sound and the `voiceEventsCtf` setting.
 */
export function playFlagVoice(action: 'taken' | 'captured' | 'returned', isOurs: boolean): void {
  if (!settings.sound) return;
  if (!(settings.game?.voiceEventsCtf ?? GAME_DEFAULTS.voiceEventsCtf)) return;
  if (!flagVoiceHowl) return;

  const eventName = action === 'taken' ? 'Taken' : action === 'captured' ? 'Captured' : 'Recovered';
  const prefix = isOurs ? 'Ours' : 'Enemy';
  const variant = Math.random() < 0.5 ? 1 : 2;
  const spriteName: FlagVoiceSpriteName = `${prefix}${eventName}_${variant}`;
  flagVoiceHowl.play(spriteName);
}

/** Toggles sound on/off, updates UI, and stops all sounds if disabling. */
export function toggle() {
  setSettings({ sound: !settings.sound });
  uiUpdateSound(true);
  stopSoundsIfPlaying();
}

/** Stops all playing sounds, voice lines included, if sound is currently disabled. */
function stopSoundsIfPlaying() {
  if (settings.sound) {
    return;
  }
  mainHowlInstance?.stop();
  flagVoiceHowl?.stop();
}

/** Gets the custom volume multiplier for a sound sprite, defaulting to 1. */
function getSpriteVolume(soundName: MainSpriteName) {
  const customVolume = customVolumeBySpriteName[soundName];
  return customVolume ?? 1;
}

/** Plays a random explosion sound for a mob, at most 4 starts a second. */
export function mobExplosion(pos: Vector, mobType: MOB_TYPES) {
  const mobTypeVolume = (explosionVolumeByMobType[mobType] ?? 0) * mobExplosionBaseVolume;
  const volume = getVolumeForCameraDistance(pos) * mobTypeVolume;
  const spriteName = explosionSpriteNames[toolsRandInt(0, 5)]!;

  // Skip if volume too low or too many explosions playing
  if (volume < MIN_AUDIBLE_VOLUME) {
    return;
  }

  if (isTooMuchSoundPlaying('mobexplosions', 4)) {
    return;
  }

  maybePlaySound(spriteName, volume, pos, toolsRand(0.8, 1.2));
}

/** Plays a random explosion sound for a player kill, at most 3 starts a second. */
export function playerKill(player: Player) {
  const planeTypeVolume = (playerKillVolumeByPlaneType[player.type] ?? 0) * playerKillBaseVolume;
  const volume = getVolumeForCameraDistance(player.pos) * planeTypeVolume;
  const spriteName = explosionSpriteNames[toolsRandInt(0, 5)]!;

  // Skip if volume too low or too many kill sounds playing
  if (volume < MIN_AUDIBLE_VOLUME) {
    return;
  }

  if (isTooMuchSoundPlaying('playerkills', 3)) {
    return;
  }

  maybePlaySound(spriteName, volume, player.pos, toolsRand(0.8, 1.2));
}

/** Plays the upgrade sound, at most 1 starts a second. */
export function playerUpgrade() {
  if (isTooMuchSoundPlaying('upgrades', 1)) {
    return;
  }

  const volume = getSpriteVolume('upgrade');
  maybePlaySound('upgrade', volume);
}

/** Plays the respawn sound; full volume for local player, distance-attenuated for others. */
export function playerRespawn(player: Player) {
  const isLocalPlayer = player.me();
  const volume = getSpriteVolume('respawn') * (isLocalPlayer ? 1 : getVolumeForCameraDistance(player.pos));
  const pos = isLocalPlayer ? null : player.pos;

  maybePlaySound('respawn', volume, pos);
}

/** Plays the game completion sound. */
export function gameComplete() {
  const volume = getSpriteVolume('complete');
  maybePlaySound('complete', volume);
}

/** Plays the level up sound. */
export function levelUp() {
  const volume = getSpriteVolume('levelup');
  maybePlaySound('levelup', volume);
}

/** Plays the UI click sound, rate-limited to 1 per 200ms. */
export function UIClick() {
  if (isTooMuchSoundPlaying('uiclick', 1, 200)) {
    return;
  }

  const volume = getSpriteVolume('click');
  maybePlaySound('click', volume);
}

/** Plays the repel effect sound at 1.5x playback rate. */
export function effectRepel(pos: Vector) {
  const volume = getVolumeForCameraDistance(pos) * repelBaseVolume;
  maybePlaySound('repel', volume, pos, 1.5);
}

/** Plays the appropriate powerup collection sound based on mob type. */
export function powerup(mobType: MOB_TYPES, pos: Vector | null) {
  const volume = (pos === null ? 1 : getVolumeForCameraDistance(pos)) * powerupBaseVolume;

  let spriteName: MainSpriteName | null = null;
  if (mobType === MOB_TYPES.UPGRADE) {
    spriteName = 'powerup_upgrade';
  } else if (mobType === MOB_TYPES.SHIELD) {
    spriteName = 'powerup_shield';
  } else if (mobType === MOB_TYPES.INFERNO) {
    spriteName = 'powerup_rampage';
  }

  if (spriteName !== null) {
    maybePlaySound(spriteName, volume * getSpriteVolume(spriteName), pos);
  }
}

/** Plays a missile launch sound, at most 5 starts a second. */
export function missileLaunch(pos: Vector, missileType: MOB_TYPES) {
  const missileTypeVolume = (launchVolumeByMissileType[missileType] ?? 0) * missileLaunchBaseVolume;
  const volume = getVolumeForCameraDistance(pos) * missileTypeVolume;
  const rate = launchPlaybackRateByMissileType[missileType] ?? 1;
  const spriteName = launchSpriteNames[toolsRandInt(0, 1)]!;

  // Skip if volume too low or too many launch sounds playing
  if (volume < MIN_AUDIBLE_VOLUME) {
    return;
  }

  if (isTooMuchSoundPlaying('launches', 5)) {
    return;
  }

  maybePlaySound(spriteName, volume, pos, rate);
}

/** Plays a player impact sound, volume scaled by impact speed and rate by the plane type. */
export function playerImpact(pos: Vector, planeType: PlaneType, impactSpeed: number) {
  const speedBasedVolume = playerImpactSpeedVolumeBoost * toolsClamp(impactSpeed, 0, 1);
  const volume = getVolumeForCameraDistance(pos) * speedBasedVolume;
  const rate = impactPlaybackRateByPlaneType[planeType] ?? 1;

  maybePlaySound('impact', volume, pos, rate);
}

/** Cleans up stale thruster sounds, throttled to once per 100ms. */
export function update() {
  // Throttle updates to once per 100ms
  if (clock.time - lastSoundscapeUpdateTime < 100) {
    return;
  }

  // Longer timeout when game doesn't have focus
  const thrusterTimeout = inputHasFocus() ? 300 : 1000;

  // Clear thrusters that haven't been updated recently
  for (const [name, thruster] of thrustersByName) {
    if (clock.time - thruster.last > thrusterTimeout) {
      clearThruster(name);
    }
  }

  // Stop and remove expired thruster fade-out sounds
  for (const [soundId, stopTime] of thrusterStopTimeBySoundId) {
    if (clock.time >= stopTime) {
      stopPlayingAParticularInstance(soundId);
      thrusterStopTimeBySoundId.delete(soundId);
    }
  }

  lastSoundscapeUpdateTime = clock.time;
}

/** Rate-limits sounds by kind, returning true if the limit is exceeded within the time window. */
function isTooMuchSoundPlaying(kind: SoundKind, max: number, ms?: number) {
  // Initialize tracking for this sound kind if not seen before
  if (playHistoryByKind[kind] == null) {
    playHistoryByKind[kind] = {
      num: 1,
      time: clock.time,
    };
    return false;
  }

  const delay = ms ?? 1000;
  const entry = playHistoryByKind[kind]!;

  // Reset counter if the time window has passed
  if (clock.time - entry.time > delay) {
    entry.num = 1;
    entry.time = clock.time;
    return false;
  }

  // Increment counter and check if limit exceeded
  entry.num++;
  return entry.num > max;
}

/** Plays a sound sprite if sound is enabled, randomizing start position for looping sounds. */
function maybePlaySound(
  spriteName: MainSpriteName,
  volume?: number | null,
  pos?: Vector | null,
  rate?: number,
  fade?: FadeParams,
) {
  if (!settings.sound) {
    return undefined;
  }

  const howl = mainHowlInstance;
  if (!howl || !howl.play) {
    return undefined;
  }

  // Skip sounds with very low volume to save processing
  if (volume != null && volume < MIN_AUDIBLE_VOLUME) {
    return undefined;
  }

  const soundId = howl.play(spriteName);

  // Randomize start position for looping sounds to avoid phase alignment
  if (spriteName === 'thruster' || spriteName === 'missile' || spriteName === 'chopper') {
    const currentSeek = howl.seek(soundId);
    howl.seek(currentSeek + toolsRand(0, 1), soundId);
  }

  applyPlayback(soundId, volume, pos, rate, fade);
  return soundId;
}

/** Applies volume, fade, rate, and stereo panning to a playing sound. */
function applyPlayback(
  soundId: number,
  volume?: number | null,
  pos?: Vector | null,
  rate?: number | null,
  fade?: FadeParams | null,
) {
  if (!settings.sound) {
    return;
  }

  const howl = mainHowlInstance;
  if (!howl) {
    return;
  }

  // Set volume if provided
  if (volume != null) {
    howl.volume(volume, soundId);
  }

  // Apply fade if provided
  if (fade != null) {
    // The fifth argument is our Howler patch: ramp from the current gain instead of jumping to `from` first
    const fromCurrentVolume = fade.length === 4 ? true : null;
    howl.fade(fade[0], fade[1], fade[2], soundId, fromCurrentVolume);
  }

  // Set playback rate if provided
  if (rate != null) {
    howl.rate(rate, soundId);
  }

  // Apply stereo panning if position provided and not on iOS
  if (pos != null && !device.ios) {
    howl.stereo(getStereoDirection(pos), soundId);
  }
}

function stopPlayingAParticularInstance(soundId: number) {
  const howl = mainHowlInstance;
  if (howl) {
    howl.stop(soundId);
  }
}

/** Fades out and schedules cleanup of a thruster sound. */
export function clearThruster(thrusterName: string) {
  const thruster = thrustersByName.get(thrusterName);
  if (!thruster) {
    return;
  }

  // Fade out the thruster sound over 200ms
  applyPlayback(thruster.soundId, null, null, null, fade4(thruster.vol, 0, 200));

  // Schedule the sound to stop completely after 300ms
  thrusterStopTimeBySoundId.set(thruster.soundId, clock.time + 300);

  // Decrement the appropriate counter (clamped to prevent negative drift)
  if (thruster.kind === 'player') {
    playerThrustersPlayingCount = Math.max(0, playerThrustersPlayingCount - 1);
  } else {
    missileThrustersPlayingCount = Math.max(0, missileThrustersPlayingCount - 1);
  }

  // Remove from active thrusters
  thrustersByName.delete(thrusterName);
}

/** Updates or starts a player thruster/rotor sound, limited to 5 simultaneous. */
export function updatePlayerThruster(player: Player) {
  if (!settings.sound) {
    return;
  }

  // Determine sprite and visibility based on plane type
  let spriteName: MainSpriteName;
  let shouldPlay: boolean;
  if (player.type === PlaneType.Mohawk) {
    shouldPlay = player.render;
    spriteName = 'chopper';
  } else {
    shouldPlay = !!(player.keystate.UP || player.keystate.DOWN);
    spriteName = 'thruster';
  }

  const thrusterName = player.thrusterName;
  const existingThruster = thrustersByName.get(thrusterName);

  if (shouldPlay) {
    if (!existingThruster) {
      // Start new thruster sound
      // Limit to 5 simultaneous player thrusters (skip for remote players if at limit)
      if (!player.me() && playerThrustersPlayingCount >= 5) {
        return;
      }

      const distanceVolume = getVolumeForCameraDistance(player.pos);
      const planeVolume = (thrusterVolumeByPlaneType[player.type] ?? 0) * playerThrusterVolume;
      const finalVolume = distanceVolume * planeVolume;
      const rateToUse = thrusterPlaybackRateByPlaneType[player.type] ?? 1;

      if (finalVolume < MIN_AUDIBLE_VOLUME) {
        return;
      }

      const soundId = maybePlaySound(spriteName, null, player.pos, rateToUse, fade3(0, finalVolume, 200));

      if (soundId !== undefined) {
        thrustersByName.set(thrusterName, {
          kind: 'player',
          started: clock.time,
          last: clock.time,
          soundId,
          vol: finalVolume,
        });

        playerThrustersPlayingCount++;
      }
    } else {
      // Update existing thruster sound
      // Throttle updates to once per 100ms
      if (clock.time - existingThruster.last < 100) {
        return;
      }

      const distanceVolume = getVolumeForCameraDistance(player.pos);
      const planeVolume = (thrusterVolumeByPlaneType[player.type] ?? 0) * playerThrusterVolume;
      let finalVolume = distanceVolume * planeVolume;

      // Boost multiplies volume by 3x
      if (player.boost) {
        finalVolume *= 3;
      }

      // Clear thruster if volume too low
      if (finalVolume < MIN_AUDIBLE_VOLUME) {
        clearThruster(thrusterName);
        return;
      }

      // Mohawk (helicopter) has speed-based playback rate adjustment
      let rateToUse: number | null = null;
      if (player.type === PlaneType.Mohawk) {
        const baseRate = thrusterPlaybackRateByPlaneType[player.type] ?? 1;
        rateToUse = baseRate + player.speed.length() / 20;
      }

      // Skip update if sound was just started (< 250ms ago) - applies to all planes
      if (clock.time - existingThruster.started < 250) {
        return;
      }

      // Update sound with smooth fade from old to new volume
      applyPlayback(
        existingThruster.soundId,
        null,
        player.pos,
        rateToUse,
        fade4(existingThruster.vol, finalVolume, 100),
      );

      existingThruster.last = clock.time;
      existingThruster.vol = finalVolume;
    }
  } else {
    // Clear thruster if not visible
    if (existingThruster) {
      clearThruster(thrusterName);
    }
  }
}

/** Updates or starts a mob/missile thruster sound, limited to 5 simultaneous. */
export function updateMobThruster(mob: Mob) {
  if (!settings.sound) {
    return;
  }

  const spriteName = 'missile';
  const thrusterName = mob.thrusterName;
  const existingThruster = thrustersByName.get(thrusterName);

  // Mobs are always "visible" (no visibility check)
  if (!existingThruster) {
    // Start new thruster sound
    // Limit to 5 simultaneous missile thrusters
    if (missileThrustersPlayingCount >= 5) {
      return;
    }

    const distanceVolume = getVolumeForCameraDistance(mob.pos);
    const finalVolume = distanceVolume * missileThrusterVolume;
    const rateToUse = thrusterPlaybackRateByMobType[mob.type] ?? 1;

    if (finalVolume < MIN_AUDIBLE_VOLUME) {
      return;
    }

    const soundId = maybePlaySound(spriteName, null, mob.pos, rateToUse, fade3(0, finalVolume, 200));

    if (soundId !== undefined) {
      thrustersByName.set(thrusterName, {
        kind: 'missile',
        started: clock.time,
        last: clock.time,
        soundId,
        vol: finalVolume,
      });

      missileThrustersPlayingCount++;
    }
  } else {
    // Update existing thruster sound
    // Throttle updates to once per 100ms
    if (clock.time - existingThruster.last < 100) {
      return;
    }

    const distanceVolume = getVolumeForCameraDistance(mob.pos);
    const finalVolume = distanceVolume * missileThrusterVolume;

    // Clear thruster if volume too low
    if (finalVolume < MIN_AUDIBLE_VOLUME) {
      clearThruster(thrusterName);
      return;
    }

    // Skip update if sound was just started (< 250ms ago) to let fade-in complete
    if (clock.time - existingThruster.started < 250) {
      return;
    }

    // Update sound with smooth fade from old to new volume
    applyPlayback(existingThruster.soundId, null, mob.pos, null, fade4(existingThruster.vol, finalVolume, 100));

    existingThruster.last = clock.time;
    existingThruster.vol = finalVolume;
  }
}

// Per-frame cached spatial values (camera + screen range), refreshed lazily
let _cachedFrameTime = -1;
let _cameraX = 0;
let _cameraY = 0;
let _range = 0;
let _rangeSq = 0;
let _horizontalRange = 0;

function refreshSpatialCache() {
  if (_cachedFrameTime === clock.time) return;
  _cachedFrameTime = clock.time;
  const camera = graphicsGetCamera();
  _cameraX = camera.x;
  _cameraY = camera.y;
  _horizontalRange = viewport.halfWidth / viewport.scale;
  _range = (_horizontalRange + viewport.halfHeight / viewport.scale) / 2;
  _rangeSq = _range * _range;
}

/** Calculates volume attenuation (0-1) based on distance from camera, linear falloff. */
function getVolumeForCameraDistance(pos: Vector) {
  refreshSpatialCache();
  const dx = pos.x - _cameraX;
  const dy = pos.y - _cameraY;

  // Skip sqrt for entities beyond audible range
  const distSq = dx * dx + dy * dy;
  if (distSq >= _rangeSq) return 0;

  const distance = Math.sqrt(distSq);

  // Linear falloff: 1.5 at center → 0.0 at range (clamped to [0, 1])
  return toolsClamp(1.5 * (1 - distance / _range), 0, 1);
}

/** Calculates stereo panning (-1 to 1) based on horizontal position relative to camera. */
function getStereoDirection(pos: Vector) {
  refreshSpatialCache();
  const horizontalOffset = pos.x - _cameraX;
  return toolsClamp((0.8 * horizontalOffset) / _horizontalRange, -1, 1);
}
